import React, { useState, useRef } from 'react';
import { Modal, List, Tag, Spin, Empty, InputNumber, Button } from 'antd';
import { Search, X } from 'lucide-react';
import { ShoppingCartOutlined } from '@ant-design/icons';
import apiService from '../services/api';
import './ProductSearch.css';

const ProductSearch = ({ onProductSelect, placeholder = "Buscar producto..." }) => {
  const [searchTerm, setSearchTerm]       = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading]         = useState(false);
  const [modalOpen, setModalOpen]         = useState(false);
  const [quantities, setQuantities]       = useState({});

  const searchInputRef = useRef(null);
  const debounceRef    = useRef(null);

  const performSearch = async (query) => {
    setIsLoading(true);
    setModalOpen(true);
    try {
      const results = await apiService.searchProducts(query);
      setSearchResults(results);
      setQuantities({});
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.trim().length >= 2) {
      debounceRef.current = setTimeout(() => performSearch(value.trim()), 350);
    } else {
      setSearchResults([]);
      setModalOpen(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchTerm.trim().length >= 2) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      performSearch(searchTerm.trim());
    }
    if (e.key === 'Escape') closeModal();
  };

  const handleSelect = (product) => {
    const qty = quantities[product.id] || 1;
    for (let i = 0; i < qty; i++) onProductSelect(product);
    closeModal();
  };

  const closeModal = () => {
    setModalOpen(false);
    setSearchResults([]);
    setSearchTerm('');
    setQuantities({});
  };

  const formatPrice = (price) => `$${Math.round(price).toLocaleString('es-AR')}`;

  const highlightMatch = (text) => {
    if (!searchTerm) return text;
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part)
        ? <mark key={i} style={{ background: '#fff3b0', padding: 0 }}>{part}</mark>
        : part
    );
  };

  return (
    <>
      <div className="product-search-container">
        <div className="search-input-wrapper">
          <Search className="search-input-icon" size={20} color="#666" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="search-input"
            autoComplete="off"
          />
          {searchTerm && (
            <button onClick={closeModal} className="search-clear-btn" type="button">
              <X size={16} />
            </button>
          )}
          {isLoading && (
            <div className="search-loading-indicator">
              <div className="loading-spinner" />
            </div>
          )}
        </div>
      </div>

      <Modal
        open={modalOpen}
        onCancel={closeModal}
        title={
          <span>
            Resultados para <strong>"{searchTerm}"</strong>
            {!isLoading && searchResults.length > 0 && (
              <span style={{ fontWeight: 400, fontSize: 13, marginLeft: 8, color: '#888' }}>
                ({searchResults.length} producto{searchResults.length !== 1 ? 's' : ''})
              </span>
            )}
          </span>
        }
        footer={null}
        width={680}
        styles={{ body: { maxHeight: '65vh', overflowY: 'auto', padding: '8px 16px' } }}
        destroyOnClose
      >
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
            <p style={{ marginTop: 12, color: '#666' }}>Buscando productos...</p>
          </div>
        ) : searchResults.length === 0 ? (
          <Empty description={`No se encontraron productos para "${searchTerm}"`} />
        ) : (
          <List
            dataSource={searchResults}
            renderItem={(product) => (
              <List.Item
                style={{ padding: '10px 4px' }}
                actions={[
                  <InputNumber
                    key="qty"
                    min={1}
                    max={99}
                    defaultValue={1}
                    size="small"
                    style={{ width: 60 }}
                    onChange={(val) =>
                      setQuantities((q) => ({ ...q, [product.id]: val || 1 }))
                    }
                  />,
                  <Button
                    key="add"
                    type="primary"
                    size="small"
                    icon={<ShoppingCartOutlined />}
                    onClick={() => handleSelect(product)}
                  >
                    Agregar
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={<span style={{ fontSize: 14 }}>{highlightMatch(product.name)}</span>}
                  description={
                    <span style={{ fontSize: 12 }}>
                      {product.productCode && (
                        <span style={{ color: '#999', marginRight: 8 }}>{product.productCode}</span>
                      )}
                      <Tag color="blue" style={{ fontSize: 11 }}>{product.category}</Tag>
                      {product.brand && <Tag style={{ fontSize: 11 }}>{product.brand}</Tag>}
                    </span>
                  }
                />
                <div style={{ fontWeight: 700, fontSize: 16, color: '#2e7d32', minWidth: 80, textAlign: 'right' }}>
                  {formatPrice(product.price)}
                </div>
              </List.Item>
            )}
          />
        )}
      </Modal>
    </>
  );
};

export default ProductSearch;
